import _ from 'lodash'

/**
 * Calculate item weight based on item rubric.
 * @param config PIE item config
 * @returns number item weight based on max rubric
 * @return For ML rubric weight calculation is : (maxPoint * number of trait) for each scale.
 */
export const calculateItemWeight = (config: any): number => {
  return config?.models?.reduce((m, model) => {
    if (model?.points?.length) {
      m = calculateLegacyRubricWeight(model)
    } else if (model?.rubricType) {
      if (model.rubricType === 'simpleRubric') {
        m = calculateSimpleRubricWeight(model)
      } else if (model.rubricType === 'multiTraitRubric') {
        m = calculateMTRWeight(model)
      }
    } else if (model?.rubrics?.simpleRubric) {
      m = calculateSimpleRubricWeight(model)
    } else if (model?.rubrics?.multiTraitRubric) {
      m = calculateMTRWeight(model)
    }
    return m
  }, 1)
}

function calculateLegacyRubricWeight(model) {
  const offset = model?.excludeZero ? 0 : 1
  return model?.points?.length - offset
}

function calculateSimpleRubricWeight(model) {
  const offset = model?.rubrics?.simpleRubric?.excludeZero ? 0 : 1
  return model?.rubrics?.simpleRubric?.points?.length - offset
}

function calculateMTRWeight(model) {
  let point = 0
  model?.rubrics?.multiTraitRubric?.scales.forEach((element) => {
    let maxPoint = element.maxPoints
    point += maxPoint * element.traits.length
  })
  return point
}

/**
 * Check if item type supports rubrics
 * @param itemType string
 * @returns boolean
 */
export const supportsRubric = (itemType: string): boolean => {
  return ['constructed_response', 'drawing_response'].includes(itemType)
}

/**
 * Check if item has rationale enabled
 * @param config PIE item config
 * @returns boolean
 */
export const hasRationale = (config: any): boolean => {
  return config?.models?.reduce((m, model) => {
    if (model?.rationaleEnabled) {
      m = model.rationaleEnabled
    }
    return m
  }, false)
}

/**
 * Check if item has partialScoring enabled
 * @param config PIE item config
 * @returns boolean
 */
export const hasPartialScoring = (config: any): boolean => {
  if (config?.models == undefined) return true
  return config?.models?.reduce((m, model) => {
    if (model?.partialScoring != undefined) {
      m = model.partialScoring
    }
    return m
  }, true)
}

/**
 * Get item rationale markup wrapped in helper classes.
 * @param config PIE item config
 * @returns string[]
 */
export const rationales = (config: any): string[] => {
  return config?.models?.reduce((m, model) => {
    if (model?.choices?.length) {
      model.choices.forEach((choice) => {
        m.push(`<span class="label">${choice.label}</span><span class="txt">${choice.rationale}</span>`)
      })
    }
    return m
  }, [])
}

/**
 * Get item correct options
 * @param config PIE item config
 * @returns string[]
 */
export const correct = (config: any): string[] => {
  return config?.models?.reduce((m, model) => {
    if (model?.choices?.length) {
      model.choices.forEach((choice) => {
        if (choice.correct) {
          m.push(choice.label)
        }
      })
    }
    return m
  }, [])
}

/**
 * Check if item has simple rubric enabled
 * @param config PIE config
 * @returns boolean
 */
export const checkIfSimpleRubric = (config: any): boolean => {
  if (config?.models == undefined) return false

  return config?.models.length > 1 && config?.models[1]['rubricType'] == 'multiTraitRubric' ? false : true
}

/**
 * Check if authoring items has Multi-level rubric.
 * @param config author Items list
 * @returns boolean
 */
export const checkForMultiLevelRubric = (authorItems: any): boolean => {
  let isItemContainsMLRubric = false
  for (let i = 0; i <= authorItems.length - 1; i++) {
    let data = authorItems[i]?.data

    if (data.itemType == 'constructed_response') {
      let type = data?.contentConfig?.models[1]?.rubricType
      if (type == 'multiTraitRubric') {
        isItemContainsMLRubric = true
      }
    }
  }
  return isItemContainsMLRubric
}

/**
 * Model of a PIE item config, only the fields the rubric helpers read
 */
export interface PieModel {
  id?: string | number
  element?: string
  rubricEnabled?: boolean
  [key: string]: unknown
}

/**
 * PIE item config, only the fields the rubric helpers read
 */
export interface PieConfig {
  elements?: { [tag: string]: string }
  models?: PieModel[]
  markup?: string
  [key: string]: unknown
}

/**
 * Check if the item config has a model using the old rubric element
 * how: an element resolves to the old rubric package and a model references it (LIB to IBX migrated CR items)
 * @param config PIE config
 * @returns boolean
 */
export const hasOldRubric = (config: PieConfig = {}): boolean => {
  if (typeof config?.elements != 'object' || !Array.isArray(config.models)) {
    return false
  }
  for (const e in config.elements) {
    if (String(config.elements[e]).includes('/rubric') && config.models.some((m) => m.element == e)) {
      return true
    }
  }
  return false
}

/**
 * Check if the item config is missing the html tag markup for the rubric
 * @param config PIE config
 * @returns boolean
 */
export const missingRubricMarkup = (config: PieConfig = {}): boolean => {
  if (typeof config?.elements == 'object' && typeof config.markup == 'string') {
    for (const e in config.elements) {
      if (String(config.elements[e]).includes('/rubric')) {
        return config.markup.search(e) == -1
      }
    }
  }
  return false
}

/**
 * Get the html tag markup for the rubric model
 * @param config PIE config
 * @returns string markup, or empty string when there is no rubric element, no model, or the model id is not a safe attribute value
 */
export const generateRubricMarkup = (config: PieConfig = {}): string => {
  let elementName = ''
  let rubricId: string | number = ''
  if (typeof config?.elements == 'object') {
    for (const e in config.elements) {
      if (String(config.elements[e]).includes('/rubric')) {
        elementName = e
        break
      }
    }
  }
  // elementName is only set when config has elements, so config is not null here
  if (elementName != '' && Array.isArray(config.models)) {
    for (const m of config.models) {
      if (m.element == elementName) {
        rubricId = m.id
      }
    }
  }
  const validId = (typeof rubricId == 'string' || typeof rubricId == 'number') && /^[\w.:-]+$/.test(String(rubricId))
  if (elementName != '' && validId) {
    return `<${elementName} id="${rubricId}"></${elementName}>`
  }
  return ''
}

/**
 * Prepare a new or duplicated item config for the author player rubric support.
 * With multi-level rubrics on, old rubric items are kept as they are and the converter is not called; the author
 * must also not be told to force a complex rubric on them (see ItemAuthorPlayer configSettings).
 * Unlike the edit path this does not check model.rubrics: new and duplicated configs never carry one.
 * Mutates the config it is given, so callers pass a copy.
 * @param config PIE config
 * @param multiLevel multi-level rubrics enabled
 * @param convert player conversion to the current rubric (addRubricToConfig)
 * @returns the config to hand to the player
 */
export const applyRubricSupport = async (
  config: PieConfig = {},
  multiLevel: boolean,
  convert: (config: PieConfig) => Promise<PieConfig>
): Promise<PieConfig> => {
  if (multiLevel && !hasOldRubric(config)) {
    for (const model of config?.models || []) {
      model.rubricEnabled = true
    }
    return config
  }
  if (missingRubricMarkup(config)) {
    config.markup += generateRubricMarkup(config)
  }
  // multiLevel is only true here for old rubric configs (the early return above took the rest);
  // PIE returns these unchanged from addRubricToConfig, so the converter is skipped
  return multiLevel ? config : convert(config)
}

const metaAllowMultiple: { [k: string]: boolean } = { bank: true }

export const tagsDisplay = (list: any[], sort: boolean = true) => {
  // create single item
  const createTag = (data: { items: any[]; name: string }): string => {
    const value = data.items ? data.items[0].name : 'N/A'
    return `${data.name}: ${value}`
  }

  // create piped tag list
  const createTagList = (data: { items: any[]; name: string }): string => {
    if (!data?.items?.length) {
      return ''
    }
    const items = data.items.map((o: any) => o.name || 'N/A').join(' | ')
    return `${data.name}s: ${items}`
  }
  const tags = (sort ? _.sortBy(list, ['name']) : list)
    .filter((o: any) => o.id !== 'media')
    .map((o: any) => (metaAllowMultiple[o.id] && o?.items?.length > 1 ? createTagList(o) : createTag(o)))
  return [...tags]
}
